import React, { useMemo } from 'react';
import { useClassNames } from 'hooks/useClassNames';
import { Button, ButtonProps } from 'primereact/button';
import { PButtonStyle as S } from './PButton.style';
import { Spinner } from 'components/Spinner/Spinner';
import * as iconsLibrary from '../../Icon/icons.library';
import { Icon } from 'components/Icon/Icon';
import { Box } from 'components/layouts/primitives/Box/Box';
import { Link, LinkProps } from 'react-router';

export type PButtonProps = Omit<ButtonProps, 'severity' | 'size' | 'icon' | 'label'> & {
  label?: React.ReactNode;
  severity?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'soft'
    | 'ghost'
    | 'invert'
    | 'danger'
    | 'highlight'
    | 'on-invert';
  size?: 'sm' | 'md' | 'lg';
  iconLeft?: keyof typeof iconsLibrary;
  iconRight?: keyof typeof iconsLibrary;
} & PButtonAsProps;

export type PButtonAsProps = {
  as?: 'a' | 'Link';
  to?: LinkProps['to'];
  href?: React.ComponentProps<'a'>['href'];
  target?: React.ComponentProps<'a'>['target'];
  rel?: React.ComponentProps<'a'>['rel'];
};

export const PButtonAsWrapper: React.FC<
  React.PropsWithChildren<PButtonAsProps & Pick<PButtonProps, 'disabled'>>
> = (props) => {
  if (props.disabled) return <>{props.children}</>;
  if (props.as === 'Link') return <Link to={props.to || ''}>{props.children}</Link>;
  if (props.as === 'a')
    return (
      <a href={props.href} target={props.target} rel={props.rel}>
        {props.children}
      </a>
    );
  return <>{props.children}</>;
};

export const PButton = React.forwardRef<Button, PButtonProps>((props, ref) => {
  const classNames = useClassNames(
    {
      [`button-size-${props.size || 'md'}`]: true,
      [`button-severity-${props.severity || 'primary'}`]: true,
    },
    props.className
  );
  const cleanProps = useMemo(
    () => (({ severity, size, label, iconLeft, iconRight, as, to, href, target, rel, ...p }) => p)(props),
    [props]
  );
  return (
    <PButtonAsWrapper
      as={props.as}
      to={props.to}
      href={props.href}
      target={props.target}
      rel={props.rel}
      disabled={props.disabled}
    >
      <S.Button type="button" {...cleanProps} className={classNames} ref={ref} loadingIcon={<Spinner />}>
        <div className="button-custom-wrapper">
          {props.children || (
            <>
              {props.iconLeft && (
                <Box className="button-custom-icon-wrapper">
                  <Icon icon={props.iconLeft} />
                </Box>
              )}
              <span className="button-custom-label">{props.label}</span>
              {props.iconRight && (
                <Box className="button-custom-icon-wrapper">
                  <Icon icon={props.iconRight} />
                </Box>
              )}
            </>
          )}
        </div>
      </S.Button>
    </PButtonAsWrapper>
  );
});
